import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useUpdateJob } from '../lib/mutations';
import { cn } from '../lib/utils';

interface NotesCellProps {
    jobId: string;
    value: string | null;
    canEdit: boolean;
    /** One line instead of four, for the compact row. */
    compact?: boolean;
}

/**
 * Inline-editable notes cell for the board.
 *
 * UX:
 *   - Default: shows current notes as plain text, or faint placeholder
 *     when empty (admins only). Hovering shows the full note as a title.
 *   - Click: switches to a textarea, cursor at end.
 *   - Blur or Cmd/Ctrl+Enter: persists via useUpdateJob if the value
 *     actually changed. No write on unchanged blur.
 *   - Escape: discards draft, exits edit mode.
 *   - Read-only role: renders as plain text, no hover, no click.
 *
 * Layout safety:
 *   - The cell uses break-all so long unbroken strings (URLs, IDs) wrap
 *     at any character and cannot force the grid column wider than its
 *     hard cap (see grid-template-columns in BoardPage / JobRow).
 *   - overflow-hidden on the button enforces clipping in display mode.
 *
 * Refetch safety: when the parent's jobs query refetches and pushes
 * a new `value` down, we sync the draft only when NOT editing, so
 * mid-edit refetches don't blow away the user's typing.
 */
export function NotesCell({ jobId, value, canEdit, compact = false }: NotesCellProps) {
    const updateMut = useUpdateJob();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<string>(value ?? '');
    const [error, setError] = useState<string | null>(null);
    const taRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (!editing) {
            setDraft(value ?? '');
            setError(null);
        }
    }, [value, editing]);

    useEffect(() => {
        if (editing && taRef.current) {
            const ta = taRef.current;
            ta.focus();
            const len = ta.value.length;
            ta.setSelectionRange(len, len);
        }
    }, [editing]);

    async function save() {
        const trimmed = draft.trim();
        const newValue = trimmed.length === 0 ? null : trimmed;
        const oldValue = value ?? null;

        if (newValue === oldValue) {
            setEditing(false);
            return;
        }

        try {
            await updateMut.mutateAsync({
                id: jobId,
                patch: { notes: newValue },
            });
            setEditing(false);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        }
    }

    function cancel() {
        setDraft(value ?? '');
        setEditing(false);
        setError(null);
    }

    function handleKey(e: KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void save();
        }
    }

    if (!canEdit) {
        return (
            <div className={cn(
                compact ? 'px-3 py-0.5 line-clamp-1' : 'px-3 py-2.5 line-clamp-4',
                'border-r border-graphite-200 dark:border-graphite-900 min-w-0 overflow-hidden text-xs text-graphite-600 dark:text-graphite-400 break-all whitespace-pre-wrap'
            )}>
                {value ? value : <span className="text-graphite-300 dark:text-graphite-700">—</span>}
            </div>
        );
    }

    if (!editing) {
        return (
            <button
                type="button"
                onClick={() => setEditing(true)}
                title={value ? value : 'Click to add notes'}
                className={cn(
                    compact ? 'px-3 py-0.5' : 'px-3 py-2.5',
                    'border-r border-graphite-200 dark:border-graphite-900 text-left text-xs min-w-0 overflow-hidden',
                    'hover:bg-graphite-100 dark:hover:bg-graphite-900 cursor-text transition-colors',
                    value
                        ? 'text-graphite-700 dark:text-graphite-300'
                        : 'text-graphite-300 dark:text-graphite-700 font-mono'
                )}
            >
                <div className={cn('whitespace-pre-wrap break-all', compact ? 'line-clamp-1' : 'line-clamp-4')}>
                    {value || 'Click to add notes...'}
                </div>
            </button>
        );
    }

    return (
        <div className="px-2 py-1.5 border-r border-graphite-200 dark:border-graphite-900 min-w-0 overflow-hidden flex flex-col gap-1">
            <textarea
                ref={taRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={save}
                onKeyDown={handleKey}
                disabled={updateMut.isPending}
                rows={3}
                placeholder="Notes..."
                className={cn(
                    'w-full text-xs bg-transparent border border-graphite-300 dark:border-graphite-700 rounded px-2 py-1.5',
                    'focus:border-signal-500 focus:outline-none focus:ring-1 focus:ring-signal-500',
                    'resize-none whitespace-pre-wrap break-all',
                    'text-graphite-900 dark:text-graphite-100',
                    'disabled:opacity-50'
                )}
            />
            {error ? (
                <div className="text-[10px] text-amber-600 dark:text-amber-400 font-mono break-all">
                    {error}
                </div>
            ) : (
                <div className="text-[9px] font-mono uppercase tracking-wider text-graphite-400 dark:text-graphite-600">
                    {updateMut.isPending ? 'Saving...' : 'Blur to save · Esc to cancel'}
                </div>
            )}
        </div>
    );
}
